import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Edit, Trash2, ArrowLeft, X, Package, Tag } from 'lucide-react';
import { Button } from '@components/Button';
import { Card, CardContent, Badge } from '@components/Card';
import { ConfirmDialog } from '@components/Dialog';
import { usePermissions } from '@hooks/usePermissions';
import { productCategoryService } from '../../services/productCategoryService';
import { ProductCategory, ProductCategoryFormData, Product } from '../../types/product';
import { ProductPickerModal } from './ProductPickerModal';

type Mode = { type: 'list' } | { type: 'create' } | { type: 'edit'; category: ProductCategory };

export const ProductCategoriesView: React.FC = () => {
  const { checkPermission } = usePermissions();
  const canCreate = checkPermission('product_categories.create').hasPermission;
  const canWrite = checkPermission('product_categories.write').hasPermission;
  const canDelete = checkPermission('product_categories.delete').hasPermission;

  const [mode, setMode] = useState<Mode>({ type: 'list' });
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ProductCategory | null>(null);
  const [deleting, setDeleting] = useState(false);

  const loadCategories = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { categories } = await productCategoryService.listCategories(1, 200, undefined, true);
      setCategories(categories);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao carregar categorias');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (mode.type === 'list') loadCategories();
  }, [mode.type, loadCategories]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await productCategoryService.deleteCategory(deleteTarget.id);
      setDeleteTarget(null);
      await loadCategories();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao excluir categoria');
    } finally {
      setDeleting(false);
    }
  };

  if (mode.type !== 'list') {
    return (
      <CategoryEditor
        category={mode.type === 'edit' ? mode.category : null}
        allCategories={categories}
        canWrite={mode.type === 'edit' ? canWrite : canCreate}
        onBack={() => setMode({ type: 'list' })}
      />
    );
  }

  const categoryName = (id?: number | null) =>
    id ? categories.find((c) => c.id === id)?.description ?? `#${id}` : '—';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-h4 flex items-center gap-2">
            <Tag size={18} /> Categorias de Produto
          </h2>
          <p className="text-small text-slate-500">Gerencie categorias e os produtos associados a elas.</p>
        </div>
        {canCreate && (
          <Button variant="primary" icon={<Plus size={16} />} onClick={() => setMode({ type: 'create' })}>
            Nova Categoria
          </Button>
        )}
      </div>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>
      )}

      <Card padding="none">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-400">Carregando…</div>
          ) : categories.length === 0 ? (
            <div className="p-8 text-center text-slate-400">Nenhuma categoria cadastrada.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-slate-600">Descrição</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-600">Categoria de personalização</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-600">Status</th>
                  <th className="text-right px-4 py-3 font-medium text-slate-600">Ações</th>
                </tr>
              </thead>
              <tbody>
                {categories.map((cat) => (
                  <tr key={cat.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3 text-slate-800">{cat.description}</td>
                    <td className="px-4 py-3 text-slate-600">{categoryName(cat.personalization_category_id)}</td>
                    <td className="px-4 py-3">
                      <Badge variant={cat.is_active ? 'success' : 'neutral'}>
                        {cat.is_active ? 'Ativa' : 'Inativa'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        {canWrite && (
                          <button
                            title="Editar"
                            className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                            onClick={() => setMode({ type: 'edit', category: cat })}
                          >
                            <Edit size={16} />
                          </button>
                        )}
                        {canDelete && (
                          <button
                            title="Excluir"
                            className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded"
                            onClick={() => setDeleteTarget(cat)}
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Excluir categoria"
        message={`Tem certeza que deseja excluir a categoria "${deleteTarget?.description}"? Os produtos associados ficarão sem categoria.`}
        severity="danger"
        confirmText="Excluir"
        loading={deleting}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------

interface CategoryEditorProps {
  category: ProductCategory | null; // null => create mode
  allCategories: ProductCategory[];
  canWrite: boolean;
  onBack: () => void;
}

const CategoryEditor: React.FC<CategoryEditorProps> = ({ category, allCategories, canWrite, onBack }) => {
  const isEdit = !!category;
  const [form, setForm] = useState<ProductCategoryFormData>({
    description: category?.description ?? '',
    personalization_category_id: category?.personalization_category_id ?? null,
    is_active: category?.is_active ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [categoryId, setCategoryId] = useState<number | null>(category?.id ?? null);

  // Associated products
  const [products, setProducts] = useState<Product[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [removingId, setRemovingId] = useState<number | null>(null);

  const loadProducts = useCallback(async () => {
    if (!categoryId) return;
    setLoadingProducts(true);
    try {
      const { products } = await productCategoryService.listCategoryProducts(categoryId, 1, 200);
      setProducts(products);
    } catch {
      // ignore
    } finally {
      setLoadingProducts(false);
    }
  }, [categoryId]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const handleSave = async () => {
    if (!form.description.trim()) {
      setError('A descrição é obrigatória.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (categoryId) {
        await productCategoryService.updateCategory(categoryId, {
          description: form.description.trim(),
          personalization_category_id: form.personalization_category_id ?? undefined,
          is_active: form.is_active,
        });
      } else {
        const created = await productCategoryService.createCategory({
          description: form.description.trim(),
          personalization_category_id: form.personalization_category_id ?? undefined,
          is_active: form.is_active,
        });
        setCategoryId(created.id);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao salvar categoria');
    } finally {
      setSaving(false);
    }
  };

  const handleAssociate = async (productIds: number[]) => {
    if (!categoryId) return;
    await productCategoryService.associateProducts(categoryId, productIds);
    await loadProducts();
  };

  const handleRemove = async (productId: number) => {
    if (!categoryId) return;
    setRemovingId(productId);
    try {
      await productCategoryService.removeProductAssociation(categoryId, productId);
      await loadProducts();
    } finally {
      setRemovingId(null);
    }
  };

  // Personalization options exclude the category itself.
  const personalizationOptions = allCategories.filter((c) => c.id !== categoryId);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="p-1.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded">
          <ArrowLeft size={18} />
        </button>
        <h2 className="text-h4">{isEdit ? 'Editar Categoria' : 'Nova Categoria'}</h2>
      </div>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>
      )}

      <Card>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Descrição <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              maxLength={120}
              value={form.description}
              disabled={!canWrite}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              className="input-field w-full px-3 py-2 text-sm"
              placeholder="Ex.: Caixa Gaveta"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Categoria de personalização</label>
            <select
              value={form.personalization_category_id ?? ''}
              disabled={!canWrite}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  personalization_category_id: e.target.value ? Number(e.target.value) : null,
                }))
              }
              className="input-field w-full px-3 py-2 text-sm"
            >
              <option value="">— Nenhuma —</option>
              {personalizationOptions.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.description}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <input
              id="cat-active"
              type="checkbox"
              checked={!!form.is_active}
              disabled={!canWrite}
              onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
              className="rounded border-slate-300"
            />
            <label htmlFor="cat-active" className="text-sm text-slate-700">
              Categoria ativa
            </label>
          </div>
        </div>

        {canWrite && (
          <div className="mt-4 flex justify-end">
            <Button variant="primary" onClick={handleSave} loading={saving}>
              {categoryId ? 'Salvar alterações' : 'Criar categoria'}
            </Button>
          </div>
        )}
      </Card>

      {/* Associated products */}
      <Card>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-h4 flex items-center gap-2">
            <Package size={16} /> Produtos associados
          </h3>
          {canWrite && (
            <Button
              variant="secondary"
              size="sm"
              icon={<Plus size={14} />}
              onClick={() => setPickerOpen(true)}
              disabled={!categoryId}
            >
              Adicionar produtos
            </Button>
          )}
        </div>

        {!categoryId ? (
          <p className="text-sm text-slate-400">Salve a categoria para associar produtos.</p>
        ) : loadingProducts ? (
          <p className="text-sm text-slate-400">Carregando produtos…</p>
        ) : products.length === 0 ? (
          <p className="text-sm text-slate-400">Nenhum produto associado a esta categoria.</p>
        ) : (
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left px-4 py-2 font-medium text-slate-600">SKU</th>
                  <th className="text-left px-4 py-2 font-medium text-slate-600">Descrição</th>
                  <th className="w-16 px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.id} className="border-t border-slate-100">
                    <td className="px-4 py-2 font-mono text-xs text-slate-700">{p.sku}</td>
                    <td className="px-4 py-2 text-slate-700">{p.description}</td>
                    <td className="px-4 py-2 text-right">
                      {canWrite && (
                        <button
                          title="Remover associação"
                          className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded disabled:opacity-50"
                          disabled={removingId === p.id}
                          onClick={() => handleRemove(p.id)}
                        >
                          <X size={16} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <ProductPickerModal
        isOpen={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onConfirm={handleAssociate}
        title="Associar produtos à categoria"
      />
    </div>
  );
};

export default ProductCategoriesView;
